import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { useIdentity } from "../lib/identity";
import { supabase } from "../lib/supabase";
import {
  fetchPortalBySlug,
  registerEmployeeByAccessCode,
  validateDeptAccessCode,
  logPortalVisit,
  type PortalData,
} from "../lib/portal-api";
import { Eye, EyeOff, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { sendEmail, buildRegistrationConfirmationEmail } from "../lib/email-helper";

export default function PortalRegisterPage() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const navigate = useNavigate();
  const { setIdentity } = useIdentity();

  const [portal, setPortal] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);

  // Registration form
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [resultOrgName, setResultOrgName] = useState<string | null>(null);
  const [resultDeptName, setResultDeptName] = useState<string | null>(null);

  useEffect(() => {
    if (!companySlug) return;
    fetchPortalBySlug(companySlug).then((data) => {
      if (data) {
        setPortal(data);
        logPortalVisit(data.id, "register");
      }
      setLoading(false);
    });
  }, [companySlug]);

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!accessCode.trim()) {
      setError("Please enter your department access code.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError("Please enter your first and last name.");
      return;
    }
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setStatus("submitting");
    setError(null);

    const fullName = `${firstName} ${lastName}`.trim();

    // Step 1: Validate the access code BEFORE creating any account
    const deptInfo = await validateDeptAccessCode(accessCode.trim());
    if (!deptInfo) {
      setError("Invalid or expired access code. Please contact your company administrator.");
      setStatus("error");
      return;
    }

    // Step 2: Create auth account
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: { data: { name: fullName } },
    });

    if (authError) {
      setError(authError.message);
      setStatus("error");
      return;
    }

    if (!authData.user) {
      setError("Registration failed — please try again.");
      setStatus("error");
      return;
    }

    // Step 3: Register employee by access code
    const result = await registerEmployeeByAccessCode(
      accessCode.trim(),
      authData.user.id,
      email.trim(),
      fullName,
    );

    if (!result) {
      setError("Registration failed during employee linkage. Please try again or contact support.");
      setStatus("error");
      return;
    }

    setResultOrgName(result.organization_name);
    setResultDeptName(result.department_name);
    setStatus("success");

    // Send confirmation email
    const { subject, body } = buildRegistrationConfirmationEmail(fullName, result.organization_name, result.department_name);
    await sendEmail({
      to: email.trim(),
      subject,
      body,
      email_type: "registration_confirmation",
    });
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-subtle)" }}>
        <div style={{ fontSize: "1.125rem", color: "var(--text-muted)" }}>Loading...</div>
      </div>
    );
  }

  if (!portal) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-subtle)" }}>
        <div style={{ textAlign: "center" }}>
          <Building2 size={48} style={{ color: "var(--text-muted)", marginBottom: "1rem" }} />
          <h1>Portal Not Found</h1>
          <Link to="/register" className="btn btn-primary" style={{ marginTop: "1rem" }}>Register a Company</Link>
        </div>
      </div>
    );
  }

  const primaryColor = portal.portal_primary_color ?? "#1e3a5f";

  return (
    <>
      <SEO
        title={`Register — ${portal.name} Portal`}
        description={`Register as an employee at ${portal.name}.`}
        canonical={`/portal/${companySlug}/register`}
      />

      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <header
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
            padding: "var(--space-4) var(--space-4)",
            color: "#fff",
          }}
        >
          <div style={{ maxWidth: "600px", margin: "0 auto", display: "flex", alignItems: "center", gap: "1rem" }}>
            {portal.logo_url ? (
              <img src={portal.logo_url} alt={portal.name} style={{ width: "40px", height: "40px", objectFit: "contain", borderRadius: "var(--radius-sm)", background: "#fff", padding: "0.25rem" }} />
            ) : (
              <Building2 size={32} />
            )}
            <div>
              <h1 style={{ color: "#fff", fontSize: "1.125rem", margin: 0 }}>{portal.name}</h1>
              <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.75rem", margin: 0 }}>Employee Registration</p>
            </div>
          </div>
        </header>

        <main style={{ flex: 1, display: "flex", justifyContent: "center", padding: "var(--space-6) var(--space-4)", background: "var(--bg-subtle)" }}>
          <div style={{ maxWidth: "480px", width: "100%" }}>
            {status === "success" ? (
              <div className="card" style={{ padding: "var(--space-6)", textAlign: "center" }}>
                <CheckCircle2 size={48} style={{ color: "var(--accent-600)", marginBottom: "1rem" }} />
                <h2 style={{ marginBottom: "0.5rem" }}>Registration Submitted</h2>
                <p className="text-muted" style={{ marginBottom: "0.5rem" }}>
                  You've registered for the <strong>{resultDeptName}</strong> department at <strong>{resultOrgName}</strong>.
                </p>
                <p className="text-muted" style={{ marginBottom: "var(--space-4)" }}>
                  Your account is pending approval from your company administrator. You'll receive an email once approved.
                </p>
                <Link to={`/portal/${companySlug}`} className="btn btn-primary">Back to Portal Login</Link>
              </div>
            ) : (
              <div className="card" style={{ padding: "var(--space-6)" }}>
                <h2 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>Employee Registration</h2>
                <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-4)" }}>
                  Register with your department access code to join {portal.name}'s training portal.
                </p>

                <form onSubmit={handleRegister}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="reg-access-code">Department Access Code</label>
                    <input
                      id="reg-access-code"
                      className="form-input"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      placeholder="8-character code from your admin"
                      maxLength={8}
                      style={{ fontFamily: "monospace", textTransform: "uppercase", letterSpacing: "0.1em" }}
                    />
                    <p className="text-muted" style={{ fontSize: "0.8125rem", marginTop: "0.375rem" }}>
                      Don't have a code? Ask your department manager or company administrator.
                    </p>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="reg-first-name">First Name</label>
                      <input
                        id="reg-first-name"
                        className="form-input"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="reg-last-name">Last Name</label>
                      <input
                        id="reg-last-name"
                        className="form-input"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="reg-email">Email</label>
                    <input
                      id="reg-email"
                      type="email"
                      className="form-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="reg-password">Password</label>
                    <div style={{ position: "relative" }}>
                      <input
                        id="reg-password"
                        type={showPassword ? "text" : "password"}
                        className="form-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        style={{ paddingRight: "2.5rem" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "0.625rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--text-muted)",
                          padding: 0,
                        }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {error && <p className="form-error">{error}</p>}

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg btn-block"
                    disabled={status === "submitting"}
                    style={{ marginTop: "var(--space-3)" }}
                  >
                    {status === "submitting" ? "Registering..." : "Register"}
                    {status !== "submitting" && <ArrowRight size={16} style={{ marginLeft: "0.5rem" }} />}
                  </button>
                </form>

                <div style={{ marginTop: "var(--space-4)", textAlign: "center" }}>
                  <Link to={`/portal/${companySlug}`} style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
                    Already have an account? Sign in
                  </Link>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
}
